import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Callout, Disclosure, Page, PageHeader, SearchField, Select, Stat, StatGrid, Switch, Table, useConfirm, useToast } from "../../../../Ui/index.jsx";
import { ChevronDown, ChevronRight } from "lucide-react";
import { apiGet, apiPost } from "../../../../Api/Client.js";
import { isJuniorType } from "../../../../Shared/ScreenUtils.jsx";
import { errorText } from "../../../../Api/errorText.js";
import "../../../../Styles/Alumni.css";

const PAGE_SIZE = 50;

function formatDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}
const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const CERTIFICATE_OPTIONS = [
  { value: "found", label: "Certificate on record" },
  { value: "none", label: "No certificate record" },
  { value: "unknown", label: "Unknown" },
];
const VISIBILITY_OPTIONS = [
  { value: "true", label: "Visible only" },
  { value: "false", label: "Hidden only" },
];

// Super-admin only, reached from inside the Admin Panel. A quiet historical
// record of former people, kept for exactly one reason: if a background
// verification company asks "did this person work here," there should be an
// honest answer. Nothing here is visible to anyone outside the company on
// its own — every row starts off, and stays off until someone here
// deliberately switches it on for that one person.
export function AlumniRecordsView() {
  const toast = useToast();
  const confirm = useConfirm();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [q, setQ] = useState("");
  const [certFilter, setCertFilter] = useState("");
  const [visFilter, setVisFilter] = useState("");
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (certFilter) p.set("certificate_status", certFilter);
    if (visFilter) p.set("portal_visible", visFilter);
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String(page * PAGE_SIZE));
    return p.toString();
  }, [q, certFilter, visFilter, page]);

  // Any filter change starts back at page 1 — otherwise a narrower search
  // can land on a page past the end of its own results.
  useEffect(() => { setPage(0); /* eslint-disable-next-line */ }, [q, certFilter, visFilter]);

  const load = () => {
    setLoading(true);
    setLoadError("");
    Promise.all([
      apiGet("/Alumni/summary/"),
      apiGet(`/Alumni/records/?${params}`),
    ]).then(([s, r]) => {
      setSummary(s);
      setRows(r.results || []);
      setCount(r.count || 0);
    }).catch((err) => {
      // A failed load must not read as "no alumni exist".
      setRows([]);
      setCount(0);
      setLoadError(errorText(err, "The alumni records could not be loaded."));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

  const toggle = async (row) => {
    setBusyId(row.id);
    try {
      const updated = await apiPost(`/Alumni/records/${row.id}/toggle/`, {});
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
      setSummary((s) => s && ({ ...s, portal_visible: s.portal_visible + (updated.portal_visible ? 1 : -1) }));
    } catch (e) {
      toast(e?.payload?.detail || e?.message || "Could not change visibility.");
    } finally {
      setBusyId(null);
    }
  };

  const bulkToggle = (makeVisible) => confirm.ask({
    title: `${makeVisible ? "Make visible" : "Hide"} ${count} ${count === 1 ? "record" : "records"}?`,
    message: `Every record matching the current filters will be ${makeVisible ? "made visible" : "hidden"}.`,
    confirmLabel: makeVisible ? "Make visible" : "Hide",
    onConfirm: () => applyBulkToggle(makeVisible),
  });

  const applyBulkToggle = async (makeVisible) => {
    try {
      await apiPost("/Alumni/records/bulk-toggle/", {
        q: q.trim() || undefined,
        certificate_status: certFilter || undefined,
        // The "Visible or not" filter narrows the change to the rows currently shown / hidden.
        visibility_filter: visFilter || undefined,
        // No filter chosen means "every record"; the server needs that said out loud.
        all: !q.trim() && !certFilter && !visFilter ? true : undefined,
        portal_visible: makeVisible,
      });
      load();
    } catch (e) {
      toast(e?.payload?.detail || e?.message || "The bulk change did not go through. Please try again.");
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const columns = [
    { key: "name", header: "Name", className: "is-card-title", render: (r) => (
      <span className="Alumni-Name">
        {expandedId === r.id ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
        <span>
          <span className="ds-strong">{r.full_name}</span>
          <small className="ds-small Alumni-Line">{r.reference_code}{isJuniorType(r.employment_type) ? "" : " · Full-time"}</small>
        </span>
      </span>
    ) },
    { key: "email", header: "Email", render: (r) => r.email },
    { key: "started_on", header: "Started", render: (r) => r.started_on || "—" },
    { key: "planned_end_on", header: "Planned end", render: (r) => r.planned_end_on || "—" },
    { key: "certificate_status", header: "Certificate", render: (r) => (
      <Badge variant={r.certificate_status === "found" ? "success" : undefined}>
        {r.certificate_status === "found" ? "On record" : r.certificate_status === "none" ? "No record" : "Unknown"}
      </Badge>
    ) },
    { key: "portal_visible", header: "Visible", className: "is-card-amount", render: (r) => (
      // A click on the row opens its detail; the switch is its own target.
      <span className="Alumni-Visible" role="presentation" onClick={(e) => e.stopPropagation()}>
        <Switch on={r.portal_visible} disabled={busyId === r.id} label={`${r.full_name} visible`}
          title={r.portal_visible ? "Visible — click to hide" : "Hidden — click to make visible"} onChange={() => toggle(r)} />
        <span className="ds-small">{r.portal_visible ? "Visible" : "Hidden"}</span>
      </span>
    ) },
  ];

  const detail = (r) => (expandedId === r.id ? (
    <div className="Alumni-Detail">
      <div>
        <h4 className="ds-label">Certificate</h4>
        {r.certificate_status === "found" ? (
          <>
            <p>Logged as sent{r.certificate_last_sent_on ? ` on ${r.certificate_last_sent_on}` : ""}, for: {(Array.isArray(r.certificate_positions) ? r.certificate_positions : []).filter(Boolean).join(", ") || "role not recorded"}.</p>
            <p className="ds-small">No certificate file exists in the legacy data for anyone — this is a log entry, not a document. There's nothing to open.</p>
          </>
        ) : (
          <p className="ds-small">No certificate log entry for this person. The legacy log never covered everyone who genuinely worked here — this alone isn't proof one was never issued.</p>
        )}
      </div>
      <div>
        <h4 className="ds-label">Best signal of when they were last around</h4>
        <p>Last logged in: {formatDateTime(r.last_login_at)}</p>
        <p>Last processed payment: {r.last_payment_processed_month ? `${MONTHS[r.last_payment_processed_month]} ${r.last_payment_processed_year}` : "no payment record found"}</p>
        <p className="ds-small">Neither is a confirmed last working day — the legacy system never recorded one. These are the closest real signals it has.</p>
      </div>
      <div>
        <h4 className="ds-label">Other details on file</h4>
        <p>{r.title || "Role not recorded"} {r.legacy_department_name ? `· ${r.legacy_department_name}` : ""}</p>
        <p>{r.phone || "No phone"}{r.whatsapp_contact && r.whatsapp_contact !== r.phone ? ` · WhatsApp ${r.whatsapp_contact}` : ""}</p>
        <p>{r.github_username ? `GitHub: ${r.github_username}` : "No GitHub on file"}</p>
        <p>{r.slack_username ? `Slack: ${r.slack_username}` : "No Slack on file"}</p>
        {r.college && <p>College: {r.college}</p>}
      </div>
    </div>
  ) : null);

  return (
    <Page>
      <PageHeader title="Alumni Records"
        subtitle={"People who worked here before August 2026 — before intranet-v2 existed. Kept so a background-verification request has a real answer, not \"we don't know.\""} />

      {summary && (
        <StatGrid>
          <Stat label="total records" value={summary.total} />
          <Stat label="visible" value={summary.portal_visible} />
          <Stat label="with a certificate on record" value={summary.with_certificate} />
          <Stat label="full-time, no certificate on record" value={summary.full_time_no_certificate} />
        </StatGrid>
      )}

      <div className="ds-toolbar">
        <SearchField className="Alumni-Search" value={q} onChange={setQ} placeholder="Search name, email or ALM- code…" />
        <Select aria-label="Certificate status" value={certFilter} onChange={(e) => setCertFilter(e.target.value)}
          placeholder="Any certificate status" options={CERTIFICATE_OPTIONS} />
        <Select aria-label="Visibility" value={visFilter} onChange={(e) => setVisFilter(e.target.value)}
          placeholder="Visible or not" options={VISIBILITY_OPTIONS} />
        <Button variant="primary" onClick={() => bulkToggle(true)}>Make matching visible</Button>
        <Button onClick={() => bulkToggle(false)}>Hide matching</Button>
      </div>

      <Disclosure quiet summary="What does Visible / Hidden mean?">
        <Callout soft>
          Every record starts <strong>Hidden</strong> — that's the whole point of this table: nothing
          in it can be seen by anyone outside the company yet. <strong>Visible</strong> is a switch for
          later, once the self-serve verification portal exists — it marks which specific people are
          allowed to show up there. Nothing is public right now regardless of this switch; the portal
          it controls hasn't been built yet.
        </Callout>
      </Disclosure>

      {loadError ? (
        <Alert tone="danger">{loadError}</Alert>
      ) : (
        <Table stack columns={columns} rows={rows} loading={loading} empty="No records match."
          onRowClick={(r) => setExpandedId(expandedId === r.id ? null : r.id)} renderDetail={detail} />
      )}

      {count > 0 && (
        <div className="ds-toolbar Alumni-Pager">
          <span className="ds-small">{page * PAGE_SIZE + 1}–{Math.min(count, (page + 1) * PAGE_SIZE)} of {count}</span>
          <Button size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
          <span className="ds-small">Page {page + 1} of {totalPages}</span>
          <Button size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}
      {confirm.dialog}
    </Page>
  );
}
